import type {
  HostCampaignConfig,
  ProcessLaunchPackageAndroid,
} from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import type { InstanceCapabilities } from '../../../services/playjector/interfaces/IPlayjectorService';
import {
  computeAndroidLaunchReplyTimeoutMs,
  type GameInstanceLaunchManager,
  type GameInstanceLaunchResult,
} from '../../game-instance/managers/GameInstanceLaunchManager';
import { buildAndroidLaunchPackage, mintAndroidStreamId } from './buildAndroidLaunchPackage';

/**
 * AndroidCampaignLauncher (android-multi-instance-host Phase 3).
 *
 * Orchestrates one android campaign launch end-to-end: fresh slate, mint the
 * streamId, build the single clamped launch package, drive the
 * reply-correlated per-instance launch through the GameInstanceLaunchManager,
 * and map the outcome onto the coordinator's hooks (campaign-switch reporting
 * + v1 machine-state signals). Owns the monotonic launch sequence used for
 * streamId minting (one launcher per GameLaunchCoordinator).
 *
 * Deliberately coordinator-free: everything it needs from the coordinator
 * arrives via the hooks object, keeping this module testable in isolation.
 */

export type AndroidLaunchInputs = {
  /** Ops-controlled maxPlayers setting (hostCapacityConfigState). */
  configMaxPlayers: number;
  /** Hardware tier cap from the gaming score; `undefined` = unknown. */
  hardwareCap: number | undefined;
  /** Machine-reported capabilities; `undefined` = unknown (ceiling ignored). */
  capabilities: InstanceCapabilities | undefined;
  /**
   * Override for the wall-clock `instanceLaunched` reply budget. When omitted,
   * scales with `instanceCount` + `readyWaitTimeMs` via
   * {@link computeAndroidLaunchReplyTimeoutMs}.
   */
  replyTimeoutMs?: number;
};

/** Coordinator-facing effects the launch flow drives, injected once. */
export type AndroidLaunchHooks = {
  /** Tear down all instances (stops v2 reporting, releases the store). */
  cleanup(hostAuthId: string): Promise<void>;
  /** Drive the v1 machine-level game state (gameLaunched true/false). */
  v1GameState(launched: boolean, gameName: string): void;
  switchSucceeded(campaignId: string): void;
  switchFailed(campaignId: string, reason: string): void;
  /** Begin v2 per-instance host state reporting (v2 campaigns only). */
  startV2Reporting(): void;
};

export type AndroidLaunchOutcome = {
  pkg: ProcessLaunchPackageAndroid;
  results: GameInstanceLaunchResult[];
  readyCount: number;
  failedCount: number;
};

export class AndroidCampaignLauncher {
  private launchSequence = 0;

  constructor(
    private readonly launchManager: GameInstanceLaunchManager,
    private readonly hooks: AndroidLaunchHooks,
  ) {}

  /**
   * Run the android campaign launch: all-failed → `switchFailed` (mirroring
   * the legacy all-instances-failed pattern); partial → succeed with the
   * failed instances recorded on the store. Throws nothing — package-build
   * and allocation failures are reported through `switchFailed`.
   */
  public async runCampaign(
    campaignId: string,
    hostAuthId: string,
    campaign: HostCampaignConfig,
    inputs: AndroidLaunchInputs,
  ): Promise<void> {
    try {
      // Fresh slate BEFORE reporting starts: hooks.cleanup stops v2 reporting
      // (cleanupGameInstances → stopReporting), so starting reporting before
      // the old campaign's teardown would self-kill it on every campaign
      // switch. launch() repeats this guard as a no-op for standalone callers.
      if (this.launchManager.getSessions().length > 0) {
        await this.hooks.cleanup(hostAuthId);
      }
      if (campaign.matchmakingVersion === 'v2') this.hooks.startV2Reporting();
      const outcome = await this.launch(campaignId, hostAuthId, campaign, inputs);
      if (outcome.readyCount === 0) {
        await this.hooks.cleanup(hostAuthId);
        this.hooks.switchFailed(campaignId, `All ${outcome.results.length} instance(s) failed to launch`);
        return;
      }
      if (outcome.failedCount > 0) {
        Sentry.logger.warn(
          `[AndroidCampaignLauncher] ${outcome.failedCount}/${outcome.results.length} android ` +
          `instances failed; ${outcome.readyCount} ready`,
        );
      }
      this.hooks.v1GameState(true, campaign.displayName);
      this.hooks.switchSucceeded(campaignId);
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      await this.hooks.cleanup(hostAuthId);
      this.hooks.switchFailed(campaignId, reason);
    }
  }

  /**
   * Build the package and launch all instances once. Throws when the campaign
   * is not launchable (missing APK package name, clamp-to-0) or the index-only
   * allocation fails; per-instance launch failures land in the outcome.
   */
  public async launch(
    campaignId: string,
    hostAuthId: string,
    campaign: HostCampaignConfig,
    inputs: AndroidLaunchInputs,
  ): Promise<AndroidLaunchOutcome> {
    // Fresh slate per campaign — a new android stream replaces the old one.
    if (this.launchManager.getSessions().length > 0) {
      await this.hooks.cleanup(hostAuthId);
    }
    this.launchSequence += 1;
    const pkg = buildAndroidLaunchPackage({
      campaign,
      streamId: mintAndroidStreamId(campaignId, this.launchSequence),
      configMaxPlayers: inputs.configMaxPlayers,
      hardwareCap: inputs.hardwareCap,
      capabilities: inputs.capabilities,
    });

    const timeoutMs =
      inputs.replyTimeoutMs ??
      computeAndroidLaunchReplyTimeoutMs(pkg.instanceCount, pkg.readyWaitTimeMs ?? 0);
    Sentry.logger.warn(
      `[AndroidCampaignLauncher] Launching android instances: campaignId=${campaignId} ` +
      `streamId=${pkg.streamId} package=${pkg.package} instanceCount=${pkg.instanceCount} ` +
      `readyWaitTimeMs=${pkg.readyWaitTimeMs ?? 0} timeoutMs=${timeoutMs}`,
    );

    const launchedAtMs = Date.now();
    const results = await this.launchManager.launchInstances(pkg, {
      campaignId,
      replyTimeoutMs: timeoutMs,
    });
    const readyCount = results.filter((result) => result.succeeded).length;
    const failed = results.filter((result) => !result.succeeded);
    Sentry.logger.warn(
      `[AndroidCampaignLauncher] Launch settled campaignId=${campaignId} streamId=${pkg.streamId} ` +
      `elapsedMs=${Date.now() - launchedAtMs} ready=${readyCount} failed=${failed.length}` +
      (failed.length > 0
        ? ` reasons=${failed.map((result) => result.failure ?? 'unknown').join(' | ')}`
        : ''),
    );
    return { pkg, results, readyCount, failedCount: results.length - readyCount };
  }
}
